/* Super Admin > Sales Data Integrity (Phase E) — read-only health checks plus a
   dry-run, flag-only pricing backfill and historical phone cleanup. Cookie-auth.
   Mounted at /app/data-integrity. */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, RefreshCw, DollarSign, Phone, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const STATUS_STYLE = {
  ok: "bg-emerald-100 text-emerald-700", overridden: "bg-blue-100 text-blue-700",
  fixable: "bg-amber-100 text-amber-700", mismatch: "bg-rose-100 text-rose-700",
  needs_input: "bg-slate-200 text-slate-600",
};

const StatCard = ({ label, value, tone = "slate", testid }) => (
  <div className={`rounded-xl border p-4 bg-white border-slate-200`} data-testid={testid}>
    <div className="text-[11.5px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
    <div className={`text-2xl font-bold text-${tone}-700`}>{value}</div>
  </div>
);

export default function DataIntegrityPage() {
  const [tab, setTab] = useState("pricing");
  const [pricing, setPricing] = useState(null);
  const [health, setHealth] = useState(null);
  const [attr, setAttr] = useState(null);
  const [phones, setPhones] = useState(null);
  const [loading, setLoading] = useState("");

  const run = async (key, fn) => { setLoading(key); try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); } finally { setLoading(""); } };

  const loadPricing = () => run("pricing", async () => setPricing((await axios.get(`${API}/api/admin/integrity/pricing`, cfg)).data));
  const loadHealth = () => run("health", async () => setHealth((await axios.get(`${API}/api/admin/integrity/sales-health`, cfg)).data));
  const loadAttr = () => run("attr", async () => setAttr((await axios.get(`${API}/api/admin/integrity/attribution`, cfg)).data));
  const previewPhones = () => run("phones", async () => setPhones((await axios.post(`${API}/api/admin/integrity/phone-cleanup`, { dry_run: true }, cfg)).data));

  const backfill = async (dryRun) => {
    if (!dryRun && !window.confirm("Apply backfill? Only empty/$0 leads with a confident derived value are filled. Mismatches are flagged for review, never overwritten. All changes are audited.")) return;
    run("backfill", async () => {
      const r = (await axios.post(`${API}/api/admin/integrity/pricing/backfill`, { dry_run: dryRun }, cfg)).data;
      if (dryRun) toast.info(`Dry run: would fill ${r.would_fill}, flag ${r.flagged_for_review}, ${r.needs_manual_input} need manual input`);
      else { toast.success(`Filled ${r.filled}, flagged ${r.flagged_for_review}`); await loadPricing(); }
    });
  };

  const applyPhones = async () => {
    if (!window.confirm("Normalize stored phone numbers to E.164? Only clearly-parseable numbers change; unparseable values are left untouched. Audited.")) return;
    run("phones", async () => {
      const r = (await axios.post(`${API}/api/admin/integrity/phone-cleanup`, { dry_run: false }, cfg)).data;
      toast.success(`Normalized ${r.changed} phone numbers`);
      await previewPhones();
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="data-integrity-page">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5 text-slate-700" />
        <h1 className="text-xl font-bold text-slate-900">Sales Data Integrity</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Read-only health checks plus a dry-run, flag-only pricing backfill and phone cleanup. Nothing is overwritten without your confirmation.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 grid grid-cols-2 gap-1 h-auto sm:inline-flex sm:flex-wrap">
          <TabsTrigger value="pricing" data-testid="integrity-tab-pricing"><DollarSign className="h-3.5 w-3.5 mr-1.5" />Pricing Integrity</TabsTrigger>
          <TabsTrigger value="health" data-testid="integrity-tab-health"><AlertTriangle className="h-3.5 w-3.5 mr-1.5" />Sales Data Health</TabsTrigger>
          <TabsTrigger value="attribution" data-testid="integrity-tab-attribution"><Users className="h-3.5 w-3.5 mr-1.5" />Attribution Health</TabsTrigger>
          <TabsTrigger value="phones" data-testid="integrity-tab-phones"><Phone className="h-3.5 w-3.5 mr-1.5" />Phone Cleanup</TabsTrigger>
        </TabsList>

        {/* Pricing Integrity */}
        <TabsContent value="pricing" className="mt-4">
          <div className="flex gap-2 mb-4">
            <Button onClick={loadPricing} disabled={loading === "pricing"} data-testid="run-pricing-check"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading === "pricing" ? "animate-spin" : ""}`} />Run Check</Button>
            {pricing && <>
              <Button variant="outline" onClick={() => backfill(true)} disabled={loading === "backfill"} data-testid="backfill-dry-run">Dry-run Backfill</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => backfill(false)} disabled={loading === "backfill" || !(pricing.summary?.fixable)} data-testid="backfill-apply">Apply Backfill</Button>
            </>}
          </div>
          {pricing && <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <StatCard label="OK" value={pricing.summary.ok} tone="emerald" testid="stat-ok" />
              <StatCard label="Overridden" value={pricing.summary.overridden} tone="blue" testid="stat-overridden" />
              <StatCard label="Fixable ($0)" value={pricing.summary.fixable} tone="amber" testid="stat-fixable" />
              <StatCard label="Mismatch" value={pricing.summary.mismatch} tone="rose" testid="stat-mismatch" />
              <StatCard label="Needs Input" value={pricing.summary.needs_input} tone="slate" testid="stat-needs-input" />
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="pricing-table">
                <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
                  <th className="text-left p-2.5">Organization</th><th className="text-left p-2.5">Type</th>
                  <th className="text-right p-2.5">Schools</th><th className="text-right p-2.5">Stored</th>
                  <th className="text-right p-2.5">System/Derived</th><th className="text-left p-2.5">Basis</th>
                  <th className="text-left p-2.5">Status</th>
                </tr></thead>
                <tbody>
                  {pricing.rows.map((r) => (
                    <tr key={r.lead_id} className="border-t border-slate-100">
                      <td className="p-2.5 font-medium text-slate-800">{r.organization}{r.is_demo && <span className="ml-1 text-[11.5px] text-slate-400">(demo)</span>}</td>
                      <td className="p-2.5 text-slate-500 capitalize">{r.lead_type}</td>
                      <td className="p-2.5 text-right">{r.projected_schools ?? "—"}</td>
                      <td className="p-2.5 text-right">{money(r.stored_value)}</td>
                      <td className="p-2.5 text-right font-medium">{money(r.system_value)}</td>
                      <td className="p-2.5 text-slate-400 text-xs">{r.basis}</td>
                      <td className="p-2.5"><Badge className={`text-[11.5px] ${STATUS_STYLE[r.status] || ""}`}>{r.status}</Badge></td>
                    </tr>
                  ))}
                  {pricing.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No sales-pipeline leads to check.</td></tr>}
                </tbody>
              </table>
            </div>
          </>}
        </TabsContent>

        {/* Sales Data Health */}
        <TabsContent value="health" className="mt-4">
          <Button onClick={loadHealth} disabled={loading === "health"} className="mb-4" data-testid="run-health-check"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading === "health" ? "animate-spin" : ""}`} />Run Health Scan</Button>
          {health && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(health.summary).filter(([k]) => k !== "total_issues").map(([k, v]) => (
                  <StatCard key={k} label={k.replace(/_/g, " ")} value={v} tone={v > 0 ? "rose" : "emerald"} testid={`health-${k}`} />
                ))}
              </div>
              {Object.entries(health.issues).map(([cat, items]) => items.length > 0 && (
                <div key={cat} className="rounded-xl border border-slate-200 p-4">
                  <div className="font-semibold text-slate-800 text-sm mb-2 capitalize">{cat.replace(/_/g, " ")} ({items.length})</div>
                  <ul className="space-y-1 text-[13px]">
                    {items.slice(0, 25).map((it, i) => (
                      <li key={i} className="flex justify-between text-slate-600">
                        <span>{it.organization}</span>
                        <span className="text-slate-400">{it.phone || (it.projected_schools != null ? `${it.projected_schools} schools` : "") || (it.unattributed_licenses ? `${it.unattributed_licenses} licenses` : "")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {health.summary.total_issues === 0 && <div className="flex items-center gap-2 text-emerald-700 text-sm"><CheckCircle2 className="h-4 w-4" />No issues found. Sales data is healthy.</div>}
            </div>
          )}
        </TabsContent>

        {/* Attribution Health */}
        <TabsContent value="attribution" className="mt-4">
          <Button onClick={loadAttr} disabled={loading === "attr"} className="mb-4" data-testid="run-attribution-check"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading === "attr" ? "animate-spin" : ""}`} />Run Attribution Report</Button>
          {attr && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-50 text-sm font-medium text-slate-700">{attr.total} sale{attr.total !== 1 ? "s" : ""}/renewal{attr.total !== 1 ? "s" : ""} missing a salesperson</div>
              <table className="w-full text-sm" data-testid="attribution-table">
                <thead className="bg-white text-slate-500 text-xs border-t"><tr><th className="text-left p-2.5">Type</th><th className="text-left p-2.5">Account</th><th className="text-left p-2.5">Detail</th><th className="text-left p-2.5">Reason</th></tr></thead>
                <tbody>
                  {attr.rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2.5"><Badge variant="outline" className="text-[11.5px]">{r.type}</Badge></td>
                      <td className="p-2.5 font-medium text-slate-800">{r.name}</td>
                      <td className="p-2.5 text-slate-500">{r.detail}</td>
                      <td className="p-2.5 text-slate-400 text-xs">{r.reason}</td>
                    </tr>
                  ))}
                  {attr.rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-emerald-600">Everything is attributed. 🎯</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Phone Cleanup */}
        <TabsContent value="phones" className="mt-4">
          <div className="flex gap-2 mb-4">
            <Button onClick={previewPhones} disabled={loading === "phones"} data-testid="preview-phone-cleanup"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading === "phones" ? "animate-spin" : ""}`} />Preview (dry-run)</Button>
            {phones && phones.would_change > 0 && <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={applyPhones} disabled={loading === "phones"} data-testid="apply-phone-cleanup">Normalize {phones.would_change} Numbers</Button>}
          </div>
          {phones && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-50 text-sm font-medium text-slate-700">
                {phones.dry_run
                  ? `${phones.would_change ?? 0} number${(phones.would_change ?? 0) !== 1 ? "s" : ""} to normalize to E.164`
                  : `Normalized ${phones.changed ?? 0} number${(phones.changed ?? 0) !== 1 ? "s" : ""} to E.164`}
              </div>
              <table className="w-full text-sm" data-testid="phone-table">
                <thead className="bg-white text-slate-500 text-xs border-t"><tr><th className="text-left p-2.5">Collection</th><th className="text-left p-2.5">Field</th><th className="text-left p-2.5">From</th><th className="text-left p-2.5">To</th></tr></thead>
                <tbody>
                  {phones.changes.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2.5 text-slate-500">{c.collection}</td><td className="p-2.5 text-slate-500">{c.field}</td>
                      <td className="p-2.5 text-slate-800">{c.from}</td><td className="p-2.5 font-medium text-emerald-700">{c.to}</td>
                    </tr>
                  ))}
                  {phones.changes.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-emerald-600">All stored phones are already E.164. ✅</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
